import { SignInIdentifier } from '@logto/schemas';
import { useContext, useEffect, useRef } from 'react';

import UserInteractionContext from '@/Providers/UserInteractionContextProvider/UserInteractionContext';
import { identifyAndSubmitInteraction, resetPasswordWithOneTimeToken } from '@/apis/experience';
import useApi from '@/hooks/use-api';
import useErrorHandler from '@/hooks/use-error-handler';
import useNavigateWithPreservedSearchParams from '@/hooks/use-navigate-with-preserved-search-params';
import LoadingLayer from '@/shared/components/LoadingLayer';
import { UserFlow } from '@/types';

type Props = {
  readonly token: string;
  readonly email: string;
  /** Invoked when the token cannot be used, to fall back to the verification code form. */
  readonly onFallback: () => void;
};

/**
 * Handles the password reset flow for existing users identified by a one-time token.
 *
 * The emailed magic link token proves the user's identity, so the flow verifies it within a
 * `ForgotPassword` interaction and goes straight to the set-new-password screen — no
 * verification code email is needed.
 *
 * The server responds to the interaction submission with `user.new_password_required_in_profile`,
 * which is the expected signal (same as in the code-based flow) to show the password screen.
 * Any other outcome (expired/consumed token, deleted user, etc.) falls back to the standard
 * verification code form.
 */
const OneTimeTokenResetPassword = ({ token, email, onFallback }: Props) => {
  const isSubmitted = useRef(false);
  const navigate = useNavigateWithPreservedSearchParams();
  const handleError = useErrorHandler();

  const asyncResetPasswordWithOneTimeToken = useApi(resetPasswordWithOneTimeToken);
  const asyncIdentifyAndSubmit = useApi(identifyAndSubmitInteraction);

  const { setIdentifierInputValue } = useContext(UserInteractionContext);

  useEffect(() => {
    (async () => {
      if (isSubmitted.current) {
        return;
      }
      // eslint-disable-next-line @silverhand/fp/no-mutation
      isSubmitted.current = true;

      const [verifyError, verificationResult] = await asyncResetPasswordWithOneTimeToken({
        token,
        identifier: { type: SignInIdentifier.Email, value: email },
      });

      if (verifyError || !verificationResult?.verificationId) {
        onFallback();
        return;
      }

      // Set email identifier to the <HiddenIdentifierInput />, so that when the user fills in
      // the new password, the browser password manager can pick up both the email and password.
      setIdentifierInputValue({ type: SignInIdentifier.Email, value: email });

      const [submitError] = await asyncIdentifyAndSubmit({
        verificationId: verificationResult.verificationId,
      });

      if (submitError) {
        await handleError(submitError, {
          'user.new_password_required_in_profile': () => {
            navigate(`/${UserFlow.ForgotPassword}/reset`, { replace: true });
          },
          global: () => {
            onFallback();
          },
        });
        return;
      }

      // A ForgotPassword interaction always requires a new password before completing, so
      // reaching this point is unexpected; fall back to the standard form.
      onFallback();
    })();
  }, [
    asyncIdentifyAndSubmit,
    asyncResetPasswordWithOneTimeToken,
    email,
    handleError,
    navigate,
    onFallback,
    setIdentifierInputValue,
    token,
  ]);

  return <LoadingLayer />;
};

export default OneTimeTokenResetPassword;
